import { useCallback, useMemo } from 'react';
import { Center, Group, Loader, Stack } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { canViewContents, useProject } from '@/api/projects';
import { useReport } from '@/api/reports';
import { reportViewerLink, reportViewerToken } from '@/api/viewer';
import { useAuth } from '@/auth/useAuth';
import { EmptyState } from '@/components/EmptyState';
import { ButtonLink } from '@/components/links';
import { usePageTitle } from '@/components/usePageTitle';
import { layerRefs, ReportPage, toReportView } from '@/features/reports/ReportPage';
import { useReportViewer } from '@/features/reports/useReportViewer';
import { ReportSettingsDrawer } from '@/features/reports/ReportSettingsDrawer';
import { ShareLinkCard } from '@/features/reports/ShareLinkCard';
import { ClientShell } from '@/shells/ClientShell';

export const Route = createFileRoute('/_client/projects_/$projectId/reports/$reportId')({ component: ClientReportRoute });

function ClientReportRoute() {
  const { t } = useTranslation('reports');
  const { projectId, reportId } = Route.useParams();
  const project = useProject(projectId);
  const report = useReport(reportId);
  const { me } = useAuth();
  const fetchToken = useCallback(() => reportViewerToken(reportId), [reportId]);
  const fetchLink = useCallback((object: string, path: string) => reportViewerLink(reportId, object, path), [reportId]);
  const { source, linkSource, credential, error: tokenError } = useReportViewer(fetchToken, fetchLink);
  const view = useMemo(() => (report.data ? toReportView(report.data) : null), [report.data]);
  const refs = useMemo(() => (view ? layerRefs(credential?.objects, view) : []), [credential, view]);
  usePageTitle(report.data?.name || t('title'));

  const editable = canViewContents(project.data?.role);

  return (
    <ClientShell eyebrow={project.data?.name} title={t('title')}>
      <Stack gap="lg">
        {report.isPending ? (
          <Center py="xl">
            <Loader color="forest" />
          </Center>
        ) : report.isError || !view ? (
          <EmptyState title={errorMessage(report.error)} />
        ) : (
          <>
            <ReportPage
              report={view}
              refs={refs}
              tokenSource={source}
              linkSource={linkSource}
              granted={credential?.objects}
              fetchLink={fetchLink}
              tokenError={!!tokenError}
              editable={editable}
              viewKey={reportId}
              actions={me?.is_staff && report.data ? <ReportSettingsDrawer report={report.data} /> : undefined}
            />
            <ShareLinkCard report={report.data} />
          </>
        )}
        <Group className="report-actions">
          <ButtonLink variant="default" to="/projects/$projectId" params={{ projectId }}>
            {t('page.back')}
          </ButtonLink>
        </Group>
      </Stack>
    </ClientShell>
  );
}
